/**
 * Previewing email attachments in place (board card drqs, round 2): a
 * thumbnail for pictures that opens the chat's image lightbox, and the
 * documents' PDF viewer in a dialog for PDFs. Which attachments qualify is
 * `attachmentPreview` (@hub/contracts) — the same rule the attachment route
 * enforces for `?inline=1`, so the UI never offers a preview the server
 * refuses.
 */
import { lazy, Suspense, useState } from 'react'
import { FileText, ImageOff, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toApiUrl } from '@/lib/orpc-client'
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

const PdfViewer = lazy(() =>
  import('@/components/pdf-viewer').then((mod) => ({ default: mod.PdfViewer }))
)

/** The attachment route; `inline` asks for the preview (picture/PDF) rendering. */
export function attachmentUrl(
  projectId: string,
  emailId: string,
  index: number,
  inline = false
): string {
  return toApiUrl(
    `/workspaces/${projectId}/inbox/${emailId}/attachments/${index}${inline ? '?inline=1' : ''}`
  )
}

/** A square thumbnail button; falls back to an icon when the picture will not load. */
export function AttachmentThumbnail({
  src,
  filename,
  onOpen,
  className,
}: {
  src: string
  filename: string
  onOpen: () => void
  className?: string
}) {
  const { t } = useTranslation('inbox')
  const [failed, setFailed] = useState(false)
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('detail.preview', { name: filename })}
      title={t('detail.preview', { name: filename })}
      data-slot="attachment-thumbnail"
      className={cn(
        'grid size-10 shrink-0 place-items-center overflow-hidden rounded border bg-muted transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        className
      )}
    >
      {failed ? (
        <ImageOff className="size-4 text-muted-foreground" />
      ) : (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="size-full object-cover"
        />
      )}
    </button>
  )
}

/** PDF attachment in the documents' viewer: full screen on a phone, a large dialog otherwise. */
export function PdfAttachmentDialog({
  url,
  filename,
  open,
  onOpenChange,
}: {
  url: string
  filename: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('inbox')
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        data-testid="pdf-attachment-dialog"
        aria-describedby={undefined}
        className="flex h-[100dvh] max-h-none w-screen max-w-none flex-col gap-0 overflow-hidden rounded-none p-0 sm:h-[90vh] sm:w-[min(96vw,1100px)] sm:max-w-none sm:rounded-lg"
      >
        <div className="flex items-center gap-2 border-b py-2 pr-2 pl-4">
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          <DialogTitle className="min-w-0 flex-1 truncate text-sm font-medium">
            {filename}
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              aria-label={t('detail.closePreview')}
              title={t('detail.closePreview')}
              className="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </DialogClose>
        </div>
        <Suspense
          fallback={
            <p className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              {t('loading')}
            </p>
          }
        >
          {open && <PdfViewer url={url} filename={filename} />}
        </Suspense>
      </DialogContent>
    </Dialog>
  )
}
